import consumer from './consumer';
import { log } from '../utils/logger';

function isFirefox() {
  return navigator.userAgent.toLowerCase().indexOf('firefox') > -1;
}

// The download_file stream is scoped per-user (not per-tab), so every tab a user has
// open receives every broadcast that user triggers anywhere. This Set tracks request_ids
// this tab itself originated (populated by the export triggers below) so `received` can
// ignore broadcasts started by other tabs. Capped so a long-lived tab doesn't leak memory.
window.__ebDownloadFilePendingRequestIds = window.__ebDownloadFilePendingRequestIds || new Set();
const MAX_PENDING_DOWNLOAD_REQUEST_IDS = 50;

function registerPendingDownloadRequestId(id) {
  const pending = window.__ebDownloadFilePendingRequestIds;
  pending.add(id);

  while (pending.size > MAX_PENDING_DOWNLOAD_REQUEST_IDS) {
    pending.delete(pending.values().next().value);
  }
}

function newDownloadRequestId() {
  const id = crypto.randomUUID();
  registerPendingDownloadRequestId(id);
  return id;
}

function tagUrlWithNewRequestId(href) {
  const url = new URL(href, window.location.origin);
  url.searchParams.set('request_id', newDownloadRequestId());
  return url.toString();
}

// Turbo Drive can re-execute this pack across navigations without a full page unload,
// stacking a duplicate subscription each time; one broadcast would then fire multiple downloads.
if (!window.__ebDownloadFileChannelSubscribed) {
  window.__ebDownloadFileChannelSubscribed = true;

  consumer.subscriptions.create('DownloadFileChannel', {
    connected() {
      log('connected to DownloadFileChannel');
    },

    disconnected() {
      log('disconnected from DownloadFileChannel');
    },

    received(data) {
      log('message received from DownloadFileChannel', data);

      let url = data.url;

      if (!url) {
        return;
      }

      // Absent request_id means a legacy/in-flight job from before this deploy — fall
      // through to the unconditional download so rolling deploys stay safe.
      if (data.request_id) {
        const pending = window.__ebDownloadFilePendingRequestIds;
        if (!pending.has(data.request_id)) {
          return; // Another tab originated this download — ignore it here.
        }
        pending.delete(data.request_id);
      }

      if (isFirefox()) {
        window.open(url, '_blank');
      } else {
        window.location.href = url;
      }
    },
  });

  // Results export dropdown (elections/_results.html.erb) — rails-ujs `data-remote` links.
  // `ajax:before` fires before rails-ujs reads the link's href to build the request.
  const RESULTS_SUMMARY_EXPORT_PATH = /\/reports\/results\/summaries(\.\w+)?$/;

  document.addEventListener('ajax:before', (event) => {
    const link = event.target;
    if (!(link instanceof HTMLAnchorElement) || !RESULTS_SUMMARY_EXPORT_PATH.test(link.pathname)) {
      return;
    }

    link.href = tagUrlWithNewRequestId(link.href);
  });

  // Voter list "Download list" links — plain rails-ujs `data-method` links with no
  // `data-remote`, so no ajax:* events fire for them. A capture-phase listener runs before
  // rails-ujs's own bubble-phase click delegate reads the href to build its hidden form.
  const VOTER_LIST_DOWNLOAD_PATH = /\/voter_list\/download(\/|$)/;

  document.addEventListener('click', (event) => {
    const link = event.target instanceof Element ? event.target.closest('a[data-method]') : null;
    if (!link || !VOTER_LIST_DOWNLOAD_PATH.test(new URL(link.href, window.location.origin).pathname)) {
      return;
    }

    link.href = tagUrlWithNewRequestId(link.href);
  }, true);

  // Billing "Download" button (legacy transaction report) — opted into Turbo via
  // `data-turbo` even though Turbo Drive is off app-wide (see packs/application.js).
  const TRANSACTION_REPORT_EXPORT_PATH = /\/accounts\/billings\/transaction_reports\//;

  document.addEventListener('turbo:before-fetch-request', (event) => {
    if (!TRANSACTION_REPORT_EXPORT_PATH.test(event.detail.url.pathname)) {
      return;
    }

    event.detail.url.searchParams.set('request_id', newDownloadRequestId());
  });
}
